import { Component, onMount, onCleanup, createSignal, Show } from 'solid-js'
import { userStore } from '/web/store'

type PaymentSuccessProps = {
  type?: 'tokens' | 'subscription'
}

const COUNTDOWN_SECONDS = 5

// #225: with the hosted-page switch, Epoch redirects the browser back here as a TOP-LEVEL page
// (no longer an in-app iframe). Token/subscription crediting happens via Epoch's server-to-server
// webhook (which also broadcasts over websocket), so we just refresh account state here, then
// auto-forward with a visible countdown.
//
// IMPORTANT: the forward is a FULL-PAGE navigation (window.location), not a client-side route.
// The SPA's Layout (web/App.tsx) treats /payment-success + /subscription-success as "iframe pages"
// and returns a header-less minimal layout via a NON-reactive early return. Because Solid components
// don't re-run on route change, a client-side navigate() away from here leaves the destination
// missing its header. A full load re-evaluates Layout cleanly (and re-fetches the fresh balance).
const PaymentSuccess: Component<PaymentSuccessProps> = (props) => {
  const isSub = props.type === 'subscription'
  const dest = isSub ? '/subscriptions' : '/'
  const [seconds, setSeconds] = createSignal(COUNTDOWN_SECONDS)

  const inIframe = () => {
    try {
      return window.self !== window.top
    } catch {
      return true
    }
  }

  const goNow = () => window.location.assign(dest)

  onMount(() => {
    const refresh = () => {
      userStore.getProfile()
      if (isSub) userStore.loadAIFansSubscriptions()
    }
    refresh()
    const timers = [setTimeout(refresh, 3000), setTimeout(refresh, 8000)]

    // Backward-compat: if this ever loads inside an old iframe checkout, tell the parent to close.
    if (inIframe()) {
      timers.push(
        setTimeout(() => {
          window.parent.postMessage(
            { type: isSub ? 'subscription-success' : 'payment-success' },
            '*'
          )
        }, 3000)
      )
      onCleanup(() => timers.forEach(clearTimeout))
      return
    }

    // Visible countdown, then auto-forward with a full-page load.
    let remaining = COUNTDOWN_SECONDS
    const tick = setInterval(() => {
      remaining -= 1
      setSeconds(remaining)
      if (remaining <= 0) {
        clearInterval(tick)
        goNow()
      }
    }, 1000)
    onCleanup(() => {
      timers.forEach(clearTimeout)
      clearInterval(tick)
    })
  })

  const message = () =>
    isSub
      ? 'Your subscription is being activated — it will appear on your account in a moment.'
      : 'Your tokens are on the way — they will be added to your balance in a moment.'

  return (
    <div class="flex h-screen w-full flex-col items-center justify-center bg-main p-4 text-center">
      <div class="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200 text-3xl">
        ✓
      </div>
      <h1 class="mb-3 text-2xl font-bold">Payment Successful!</h1>
      <p class="max-w-md text-gray-300">{message()}</p>

      <Show
        when={!inIframe()}
        fallback={<p class="mt-4 text-sm text-gray-400">This window will close automatically...</p>}
      >
        <button
          onClick={goNow}
          class="mt-8 rounded-lg bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200 px-8 py-3 font-semibold text-white"
        >
          Continue now
        </button>
        <p class="mt-4 text-sm text-gray-400">
          Redirecting in <span class="font-bold text-white">{seconds()}</span>{' '}
          {seconds() === 1 ? 'second' : 'seconds'}…
        </p>
      </Show>
    </div>
  )
}

export default PaymentSuccess
